import PropTypes from 'prop-types';

function formatCents(cents) {
  return `$${(cents / 100).toFixed(2)}`;
}

const ORDER_FIELDS = [
  {
    key: 'courtesy_order',
    label: 'Courtesy Order',
    format: (v) => (v ? 'Yes' : 'No'),
  },
  {
    key: 'paid_by_partner',
    label: 'Paid By Partner',
    format: (v) => (v ? 'Yes' : 'No'),
  },
  { key: 'subtotal', label: 'Subtotal', format: formatCents },
  { key: 'total', label: 'Total', format: formatCents },
  { key: 'shipping_amount', label: 'Shipping', format: formatCents },
  { key: 'topline_revenue', label: 'Top Line Revenue', format: formatCents },
  { key: 'earned_revenue', label: 'Earned Revenue', format: formatCents },
  {
    key: 'partner_revenue_platform',
    label: 'Partner Rev. (Platform)',
    format: formatCents,
  },
  {
    key: 'partner_revenue_office',
    label: 'Partner Rev. (Office)',
    format: formatCents,
  },
  {
    key: 'partner_revenue_total',
    label: 'Total Partner Rev.',
    format: formatCents,
  },
  { key: 'partner_fee', label: 'RC Partner Fees', format: formatCents },
];

const ITEM_FIELDS = [
  { key: 'price', label: 'Price', format: formatCents },
  { key: 'total', label: 'Total', format: formatCents },
  { key: 'topline_revenue', label: 'Top Line Rev.', format: formatCents },
  { key: 'earned_revenue', label: 'Earned Rev.', format: formatCents },
  {
    key: 'partner_revenue_platform',
    label: 'Platform Rev.',
    format: formatCents,
  },
  { key: 'partner_revenue_office', label: 'Office Rev.', format: formatCents },
  {
    key: 'partner_revenue_total',
    label: 'Total Partner Rev.',
    format: formatCents,
  },
  { key: 'partner_fee', label: 'Partner Fee', format: formatCents },
];

function hasChanged(current, converted, key) {
  return current[key] !== converted[key];
}

function ConvertOrderPreview({ previewData }) {
  const { current, converted } = previewData;

  return (
    <div className="space-y-6">
      <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
        <h3 className="mb-4 font-semibold text-gray-900 text-sm">
          Order Values
        </h3>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-gray-200 border-b">
              <th className="py-2 text-left font-medium text-gray-500">
                Field
              </th>
              <th className="py-2 text-right font-medium text-gray-500">
                Current
              </th>
              <th className="py-2 text-right font-medium text-gray-500">
                After Conversion
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {ORDER_FIELDS.map(({ key, label, format }) => {
              const changed = hasChanged(current, converted, key);
              return (
                <tr key={key}>
                  <td className="py-2 text-gray-700">{label}</td>
                  <td
                    className={`py-2 text-right ${changed ? 'text-red-600 line-through' : 'text-gray-900'}`}
                  >
                    {format(current[key])}
                  </td>
                  <td
                    className={`py-2 text-right ${changed ? 'font-medium text-green-600' : 'text-gray-900'}`}
                  >
                    {format(converted[key])}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {current.order_items?.length > 0 && (
        <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
          <h3 className="mb-4 font-semibold text-gray-900 text-sm">
            Order Items
          </h3>
          {current.order_items.map((currentItem, index) => {
            const convertedItem = converted.order_items[index];
            return (
              <div key={currentItem.id} className="mb-4 last:mb-0">
                <p className="mb-2 font-medium text-gray-800 text-xs">
                  {currentItem.name}
                </p>
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-gray-200 border-b">
                      <th className="py-1 text-left font-medium text-gray-500">
                        Field
                      </th>
                      <th className="py-1 text-right font-medium text-gray-500">
                        Current
                      </th>
                      <th className="py-1 text-right font-medium text-gray-500">
                        After
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {ITEM_FIELDS.map(({ key, label, format }) => {
                      const changed = hasChanged(
                        currentItem,
                        convertedItem,
                        key,
                      );
                      return (
                        <tr key={key}>
                          <td className="py-1 text-gray-600">{label}</td>
                          <td
                            className={`py-1 text-right ${changed ? 'text-red-600 line-through' : 'text-gray-900'}`}
                          >
                            {format(currentItem[key])}
                          </td>
                          <td
                            className={`py-1 text-right ${changed ? 'font-medium text-green-600' : 'text-gray-900'}`}
                          >
                            {format(convertedItem[key])}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

ConvertOrderPreview.propTypes = {
  previewData: PropTypes.object.isRequired,
};

export default ConvertOrderPreview;
